import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
// Removed tabs import - now using single page layout
import { Separator } from "@/components/ui/separator";
import {
  Bot,
  MessageSquare,
  Settings,
  FileText,
  AlertCircle,
  Plus,
  X,
  Info
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useBuilder } from "@/contexts/BuilderContext";
import { AIChatModeConfig } from "@/hooks/useAIChatModeConfig";
import { DEFAULT_UNIFIED_SETTINGS } from "@/types/builder";

// No props needed - using unified context instead
interface AIChatModeSettingsProps {}

// Predefined category IDs for dropdown
const PREDEFINED_CATEGORIES = [
  { id: 'insurance', label: 'Insurance' },
  { id: 'travel', label: 'Travel' },
  { id: 'mortgage', label: 'Mortgage' },
  { id: 'healthcare', label: 'Healthcare' },
  { id: 'automotive', label: 'Automotive' },
  { id: 'real-estate', label: 'Real Estate' },
  { id: 'finance', label: 'Finance' },
  { id: 'education', label: 'Education' },
  { id: 'technology', label: 'Technology' },
  { id: 'legal', label: 'Legal' },
  { id: 'business', label: 'Business' },
  { id: 'personal', label: 'Personal' }
] as const;

const PREDEFINED_CATEGORY_IDS = PREDEFINED_CATEGORIES.map(cat => cat.id);

export const AIChatModeSettings: React.FC<AIChatModeSettingsProps> = () => {
  const { toast } = useToast();
  
  // Get data from unified context
  const { getChatbot, getSettings, updateSettings, state, debouncedSave } = useBuilder();
  const chatbot = getChatbot();
  const settings = getSettings();
  
  // Get AI config from unified settings with proper defaults
  const config = settings?.ai_chat_config || DEFAULT_UNIFIED_SETTINGS.ai_chat_config;
  
  // Helper function to update config
  const updateConfig = (updates: Partial<AIChatModeConfig>) => {
    console.log('=== UPDATE CONFIG DEBUG ===');
    console.log('updateConfig called with:', updates);
    console.log('Current config before update:', config);
    const newConfig = { ...config, ...updates };
    console.log('New config after merge:', newConfig);
    console.log('Calling updateSettings with ai_chat_config:', newConfig);
    updateSettings({ ai_chat_config: newConfig });
    console.log('=== END UPDATE CONFIG DEBUG ===');
  };
  
  // Handle welcome message from chatbot
  const [welcomeMessage, setWelcomeMessage] = useState(chatbot?.welcome_message || '');

  // Update welcome message when chatbot data changes
  useEffect(() => {
    if (chatbot?.welcome_message !== undefined) {
      setWelcomeMessage(chatbot.welcome_message);
    }
  }, [chatbot?.welcome_message]);

  const handleWelcomeMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setWelcomeMessage(e.target.value);
  };

  const handleWelcomeMessageBlur = () => {
    // Update the chatbot welcome message in the context
    if (chatbot && welcomeMessage !== chatbot.welcome_message) {
      console.log('AI Settings: Updating welcome message from blur event:', welcomeMessage);
      // Save welcome message directly to chatbot, not in settings
      debouncedSave({
        welcome_message: welcomeMessage
      });
    }
  };
  
  // Loading and saving states
  const isLoading = state.isLoading;
  const isSaving = state.isSaving;
  const showWelcomeSuccess = false; // This can be managed by parent if needed
  
  // Save function - manually trigger save since auto-save is disabled
  const handleSave = () => {
    console.log('AI Settings save button clicked!', { isDirty: state.isDirty, chatbot: !!state.chatbot });

    if (state.chatbot) {
      const chatbot = state.chatbot;
      const settings = state.chatbot.settings;

      console.log('=== DETAILED SAVE DEBUG ===');
      console.log('Current config from component:', config);
      console.log('AI Settings saving with settings:', settings);
      console.log('AI config in settings:', settings.ai_chat_config);
      console.log('Assistant name in config:', settings.ai_chat_config?.aiPersonality?.name);
      console.log('Assistant name from component config:', config.aiPersonality.name);
      console.log('Welcome message from component state:', welcomeMessage);
      console.log('Welcome message from chatbot:', chatbot.welcome_message);
      console.log('=== END SAVE DEBUG ===');

      debouncedSave({
        settings: settings,
        name: chatbot.name,
        description: chatbot.description,
        title: chatbot.title,
        welcome_message: welcomeMessage, // Use the current welcome message state
        completion_message: chatbot.completion_message
      });

      console.log('AI Settings: Saving welcome message in manual save:', welcomeMessage);

      toast({
        title: "Saving changes...",
        description: "AI chat configuration is being saved."
      });
    } else {
      console.log('AI Settings: No chatbot data available for save');
    }
  };

  // Helper function to ensure field format consistency with stable IDs
  const normalizeFields = (fields: any[]): Array<{ id: string; name: string; description: string }> => {
    return fields.map((field, index) => {
      if (typeof field === 'string') {
        return { id: `field-${Date.now()}-${index}`, name: field, description: '' };
      }
      // Add ID if missing
      if (!field.id) {
        return { ...field, id: `field-${Date.now()}-${index}` };
      }
      return field;
    });
  };

  // Normalize fields on component mount to handle legacy string arrays
  useEffect(() => {
    const hasStringFields = config.requiredFields.some(field => typeof field === 'string' || !field.id) ||
                           config.optionalFields.some(field => typeof field === 'string' || !field.id);

    if (hasStringFields) {
      console.log('🔄 Normalizing fields with stable IDs');
      updateConfig({
        requiredFields: normalizeFields(config.requiredFields),
        optionalFields: normalizeFields(config.optionalFields)
      });
    }
  }, []); // Only run once on mount

  // Helper function to add/remove array items
  const addArrayItem = (field: keyof AIChatModeConfig, item: string) => {
    const currentArray = config[field] as string[];
    updateConfig({ [field]: [...currentArray, item] });
  };

  const removeArrayItem = (field: keyof AIChatModeConfig, index: number) => {
    const currentArray = config[field] as any[];
    updateConfig({ [field]: currentArray.filter((_, i) => i !== index) });
  };

  // Helper function for field objects specifically with stable IDs
  const addFieldItem = (field: 'requiredFields' | 'optionalFields') => {
    const currentArray = config[field] as Array<{ id: string; name: string; description: string }>;
    const newField = { id: `field-${Date.now()}-${Math.random()}`, name: '', description: '' };
    console.log(`➕ Adding new field to ${field}:`, newField);
    updateConfig({ [field]: [...currentArray, newField] });
  };

  const removeFieldItem = (field: 'requiredFields' | 'optionalFields', index: number) => {
    const currentArray = config[field] as Array<{ id: string; name: string; description: string }>;
    console.log(`➖ Removing field from ${field} at index ${index}`);
    updateConfig({ [field]: currentArray.filter((_, i) => i !== index) });
  };

  // Helper function to update field safely
  const updateFieldItem = (field: 'requiredFields' | 'optionalFields', index: number, updates: Partial<{ name: string; description: string }>) => {
    const currentArray = config[field] as Array<{ id: string; name: string; description: string }>;
    const newArray = [...currentArray];
    newArray[index] = { ...newArray[index], ...updates };
    console.log(`✏️ Updating field in ${field} at index ${index}:`, updates);
    updateConfig({ [field]: newArray });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Bot className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">AI-Only Chat Configuration</h2>
            <p className="text-sm text-muted-foreground">
              Configure your AI assistant's personality, behavior, and conversation guidelines
            </p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>



      {/* 2-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Left Column - Core Configuration */}
        <div className="space-y-6">
          {/* AI Personality Section */}
          <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Bot className="h-5 w-5" />
            <span>AI Personality</span>
          </CardTitle>
          <CardDescription>
            Define your AI assistant's identity and conversation style
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ai-name">Assistant Name</Label>
                  <Input
                    id="ai-name"
                    value={config.aiPersonality.name}
                    onChange={(e) => updateConfig({
                      aiPersonality: { ...config.aiPersonality, name: e.target.value }
                    })}
                    placeholder="e.g., Alex, Customer Support Bot"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ai-role">Role/Title</Label>
                  <Input
                    id="ai-role"
                    value={config.aiPersonality.role}
                    onChange={(e) => updateConfig({
                      aiPersonality: { ...config.aiPersonality, role: e.target.value }
                    })}
                    placeholder="e.g., Customer Service Representative"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="ai-description">Personality Description</Label>
                <Textarea
                  id="ai-description"
                  value={config.aiPersonality.description}
                  onChange={(e) => updateConfig({
                    aiPersonality: { ...config.aiPersonality, description: e.target.value }
                  })}
                  placeholder="Describe your AI's personality, expertise, and approach to conversations..."
                  rows={3}
                />
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Conversation Style</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Tone</Label>
                    <select 
                      className="w-full p-2 border rounded-md"
                      value={config.conversationStyle.tone}
                      onChange={(e) => updateConfig({
                        conversationStyle: { 
                          ...config.conversationStyle, 
                          tone: e.target.value as any 
                        }
                      })}
                    >
                      <option value="professional">Professional</option>
                      <option value="friendly">Friendly</option>
                      <option value="casual">Casual</option>
                      <option value="formal">Formal</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Response Length</Label>
                    <select 
                      className="w-full p-2 border rounded-md"
                      value={config.conversationStyle.responseLength}
                      onChange={(e) => updateConfig({
                        conversationStyle: { 
                          ...config.conversationStyle, 
                          responseLength: e.target.value as any 
                        }
                      })}
                    >
                      <option value="concise">Concise</option>
                      <option value="detailed">Detailed</option>
                      <option value="adaptive">Adaptive</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Proactiveness</Label>
                    <select 
                      className="w-full p-2 border rounded-md"
                      value={config.conversationStyle.proactiveness}
                      onChange={(e) => updateConfig({
                        conversationStyle: { 
                          ...config.conversationStyle, 
                          proactiveness: e.target.value as any 
                        }
                      })}
                    >
                      <option value="reactive">Reactive</option>
                      <option value="balanced">Balanced</option>
                      <option value="proactive">Proactive</option>
                    </select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

      {/* System Instructions Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>System Instructions</span>
          </CardTitle>
          <CardDescription>
            Define how your AI should behave, respond to users, and greet them initially
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
                       {/* Welcome Message Section */}
               <div className="space-y-2">
                 <Label htmlFor="welcome-message">Welcome Message</Label>
                 {showWelcomeSuccess && (
                   <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded relative mb-2">
                     <span className="block sm:inline">Welcome message saved successfully!</span>
                   </div>
                 )}
                 <Textarea
                   id="welcome-message"
                   value={welcomeMessage}
                   onChange={handleWelcomeMessageChange}
                   onBlur={handleWelcomeMessageBlur}
                   placeholder="Hello! I'm your AI assistant. How can I help you today?"
                   rows={3}
                 />
                 <p className="text-xs text-muted-foreground">
                   The initial message users will see when they start an AI conversation.
                 </p>
               </div>
               </CardContent>

               <Separator />
            <CardContent className="space-y-4">
               <div className="space-y-2">
                 <Label htmlFor="system-instructions">Core Instructions</Label>
                 <Textarea
                   id="system-instructions"
                   value={config.systemInstructions}
                   onChange={(e) => updateConfig({ systemInstructions: e.target.value })}
                   placeholder="Provide detailed instructions on how the AI should behave, what it should focus on, and how it should help users..."
                   rows={6}
                 />
                 <p className="text-xs text-muted-foreground">
                   These instructions will be used as the system prompt for your AI assistant.
                 </p>
               </div>

               <Separator />



              <div className="space-y-3">
                <Label>Conversation Guidelines</Label>
                <div className="space-y-2">
                  {config.conversationGuidelines.map((guideline, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Input
                        value={guideline}
                        onChange={(e) => {
                          const newGuidelines = [...config.conversationGuidelines];
                          newGuidelines[index] = e.target.value;
                          updateConfig({ conversationGuidelines: newGuidelines });
                        }}
                        placeholder="Enter a conversation guideline..."
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeArrayItem('conversationGuidelines', index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addArrayItem('conversationGuidelines', '')}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Guideline
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Prohibited Topics</Label>
                <div className="space-y-2">
                  {config.prohibitedTopics.map((topic, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Input
                        value={topic}
                        onChange={(e) => {
                          const newTopics = [...config.prohibitedTopics];
                          newTopics[index] = e.target.value;
                          updateConfig({ prohibitedTopics: newTopics });
                        }}
                        placeholder="Enter a prohibited topic..."
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeArrayItem('prohibitedTopics', index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addArrayItem('prohibitedTopics', '')}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Prohibited Topic
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Data & Advanced Settings */}
        <div className="space-y-6">
          {/* Data Collection Section */}
          <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <MessageSquare className="h-5 w-5" />
            <span>Data Collection</span>
          </CardTitle>
              <CardDescription>
                Configure what user information to collect during conversations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Collect User Information</Label>
                  <p className="text-sm text-muted-foreground">
                    Enable automatic collection of user details during conversation
                  </p>
                </div>
                <Switch
                  checked={config.collectUserInfo}
                  onCheckedChange={(checked) => updateConfig({ collectUserInfo: checked })}
                />
              </div>

              {config.collectUserInfo && (
                <>
                  <Separator />

                  <div className="space-y-3">
                    <Label>Required Fields</Label>
                    <div className="space-y-2">
                      {config.requiredFields.map((field, index) => {
                        const fieldObj = typeof field === 'string' ? { id: `legacy-${index}`, name: field, description: '' } : field;
                        const fieldId = fieldObj.id || `field-${index}`;
                        return (
                          <div key={fieldId} className="space-y-2 p-3 border rounded-lg">
                            <div className="flex items-center space-x-2">
                              <Input
                                value={fieldObj.name}
                                onChange={(e) => {
                                  const newFields = [...config.requiredFields];
                                  newFields[index] = typeof field === 'string'
                                    ? { name: e.target.value, description: '' }
                                    : { ...field, name: e.target.value };
                                  updateConfig({ requiredFields: newFields });
                                }}
                                placeholder="Field name (e.g., name, email, company)"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => removeFieldItem('requiredFields', index)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            <Input
                              value={fieldObj.description}
                              onChange={(e) => {
                                const newFields = [...config.requiredFields];
                                newFields[index] = typeof field === 'string'
                                  ? { name: field, description: e.target.value }
                                  : { ...field, description: e.target.value };
                                updateConfig({ requiredFields: newFields });
                              }}
                              placeholder="Field description (e.g., 'I need to know the budget')"
                              className="text-sm"
                            />
                          </div>
                        );
                      })}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addFieldItem('requiredFields')}
                        className="w-full"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Required Field
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>Optional Fields</Label>
                    <div className="space-y-2">
                      {config.optionalFields.map((field, index) => {
                        const fieldObj = typeof field === 'string' ? { id: `legacy-${index}`, name: field, description: '' } : field;
                        const fieldId = fieldObj.id || `field-${index}`;
                        return (
                          <div key={fieldId} className="space-y-2 p-3 border rounded-lg">
                            <div className="flex items-center space-x-2">
                              <Input
                                value={fieldObj.name}
                                onChange={(e) => {
                                  const newFields = [...config.optionalFields];
                                  newFields[index] = typeof field === 'string'
                                    ? { name: e.target.value, description: '' }
                                    : { ...field, name: e.target.value };
                                  updateConfig({ optionalFields: newFields });
                                }}
                                placeholder="Field name (e.g., phone, department, budget)"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => removeFieldItem('optionalFields', index)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            <Input
                              value={fieldObj.description}
                              onChange={(e) => {
                                const newFields = [...config.optionalFields];
                                newFields[index] = typeof field === 'string'
                                  ? { name: field, description: e.target.value }
                                  : { ...field, description: e.target.value };
                                updateConfig({ optionalFields: newFields });
                              }}
                              placeholder="Field description (e.g., 'I need to know their budget range')"
                              className="text-sm"
                            />
                          </div>
                        );
                      })}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addFieldItem('optionalFields')}
                        className="w-full"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Optional Field
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Multi-Offer Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Multi-Offer Support
              </CardTitle>
              <CardDescription>
                Configure support for multiple offer categories in a single conversation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Enable Multi-Offer Support</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow AI to handle multiple topics/offers in one conversation
                  </p>
                </div>
                <Switch
                  checked={config.offers?.enabled || false}
                  onCheckedChange={(checked) => updateConfig({ 
                    offers: { 
                      ...config.offers, 
                      enabled: checked 
                    } 
                  })}
                />
              </div>

              {config.offers?.enabled && (
                <>
                  <Separator />
                  


                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Offer Categories</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newCategory = {
                            id: 'business',
                            name: 'New Category',
                            description: 'Description for new category',
                            keywords: ['keyword1', 'keyword2'],
                            requiredFields: [{ name: 'name', description: 'Your name' }],
                            optionalFields: [],
                            customInstructions: 'Custom instructions for this category'
                          };
                          updateConfig({ 
                            offers: { 
                              ...config.offers, 
                              categories: [...(config.offers?.categories || []), newCategory] 
                            } 
                          });
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Category
                      </Button>
                    </div>

                    {config.offers?.categories?.map((category, categoryIndex) => (
                      <Card key={category.id} className="p-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="grid grid-cols-2 gap-2 flex-1">
                              <div>
                                <Label>Category Name</Label>
                                <Input
                                  value={category.name}
                                  onChange={(e) => {
                                    const newCategories = [...(config.offers?.categories || [])];
                                    newCategories[categoryIndex] = { ...category, name: e.target.value };
                                    updateConfig({ 
                                      offers: { 
                                        ...config.offers, 
                                        categories: newCategories 
                                      } 
                                    });
                                  }}
                                  placeholder="e.g., Insurance"
                                />
                              </div>
                              <div>
                                <Label>Category ID</Label>
                                <Select
                                  value={category.id}
                                  onValueChange={(value) => {
                                    const newCategories = [...(config.offers?.categories || [])];
                                    newCategories[categoryIndex] = { ...category, id: value };
                                    updateConfig({ 
                                      offers: { 
                                        ...config.offers, 
                                        categories: newCategories 
                                      } 
                                    });
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select category ID" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {PREDEFINED_CATEGORIES.map((category) => (
                                      <SelectItem key={category.id} value={category.id}>{category.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {!PREDEFINED_CATEGORY_IDS.includes(category.id as any) && (
                                  <div className="mt-2">
                                    <Label className="text-sm text-muted-foreground">Custom ID</Label>
                                    <Input
                                      value={category.id}
                                      onChange={(e) => {
                                        const newCategories = [...(config.offers?.categories || [])];
                                        newCategories[categoryIndex] = { ...category, id: e.target.value };
                                        updateConfig({ 
                                          offers: { 
                                            ...config.offers, 
                                            categories: newCategories 
                                          } 
                                        });
                                      }}
                                      placeholder="Enter custom category ID"
                                      className="text-sm"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const newCategories = (config.offers?.categories || []).filter((_, i) => i !== categoryIndex);
                                updateConfig({ 
                                  offers: { 
                                    ...config.offers, 
                                    categories: newCategories 
                                  } 
                                });
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>

                          <div>
                            <Label>Description</Label>
                            <Input
                              value={category.description}
                              onChange={(e) => {
                                const newCategories = [...(config.offers?.categories || [])];
                                newCategories[categoryIndex] = { ...category, description: e.target.value };
                                updateConfig({ 
                                  offers: { 
                                    ...config.offers, 
                                    categories: newCategories 
                                  } 
                                });
                              }}
                              placeholder="Brief description of this category"
                            />
                          </div>

                          <div>
                            <Label>Keywords (comma-separated)</Label>
                            <Input
                              value={category.keywords.join(', ')}
                              onChange={(e) => {
                                const keywords = e.target.value.split(',').map(k => k.trim()).filter(k => k);
                                const newCategories = [...(config.offers?.categories || [])];
                                newCategories[categoryIndex] = { ...category, keywords };
                                updateConfig({ 
                                  offers: { 
                                    ...config.offers, 
                                    categories: newCategories 
                                  } 
                                });
                              }}
                              placeholder="e.g., insurance, coverage, policy, premium"
                            />
                          </div>

                          <div>
                            <Label>Custom Instructions</Label>
                            <Textarea
                              value={category.customInstructions || ''}
                              onChange={(e) => {
                                const newCategories = [...(config.offers?.categories || [])];
                                newCategories[categoryIndex] = { ...category, customInstructions: e.target.value };
                                updateConfig({ 
                                  offers: { 
                                    ...config.offers, 
                                    categories: newCategories 
                                  } 
                                });
                              }}
                              placeholder="Specific instructions for handling this category"
                              rows={2}
                            />
                          </div>
                        </div>
                      </Card>
                    ))}

                    {(!config.offers?.categories || config.offers.categories.length === 0) && (
                      <div className="text-center py-8 text-muted-foreground">
                        <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No offer categories configured</p>
                        <p className="text-sm">Add categories to enable multi-offer support</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

      {/* Advanced Settings Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Advanced Settings</span>
          </CardTitle>
              <CardDescription>
                Configure advanced conversation management and behavior
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fallback-message">Fallback Message</Label>
                <Textarea
                  id="fallback-message"
                  value={config.fallbackMessage}
                  onChange={(e) => updateConfig({ fallbackMessage: e.target.value })}
                  placeholder="Message to show when the AI cannot understand or respond appropriately..."
                  rows={2}
                />
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Context Memory</Label>
                    <p className="text-sm text-muted-foreground">
                      Remember conversation history
                    </p>
                  </div>
                  <Switch
                    checked={config.contextMemory}
                    onCheckedChange={(checked) => updateConfig({ contextMemory: checked })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max-length">Max Conversation Length</Label>
                  <Input
                    id="max-length"
                    type="number"
                    value={config.maxConversationLength}
                    onChange={(e) => updateConfig({ maxConversationLength: parseInt(e.target.value) || 50 })}
                    placeholder="50"
                    min="10"
                    max="200"
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximum number of messages in conversation
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <Label>End Conversation Triggers</Label>
                <p className="text-sm text-muted-foreground">
                  Keywords or phrases that should end the conversation
                </p>
                <div className="space-y-2">
                  {config.endConversationTriggers.map((trigger, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Input
                        value={trigger}
                        onChange={(e) => {
                          const newTriggers = [...config.endConversationTriggers];
                          newTriggers[index] = e.target.value;
                          updateConfig({ endConversationTriggers: newTriggers });
                        }}
                        placeholder="e.g., goodbye, end chat, no thanks"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeArrayItem('endConversationTriggers', index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addArrayItem('endConversationTriggers', '')}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add End Trigger
                  </Button>
                </div>
              </div>

              {/* Escalation Triggers - Hidden for now */}
              {false && (
                <div className="space-y-3">
                  <Label>Escalation Triggers</Label>
                  <p className="text-sm text-muted-foreground">
                    Keywords that should escalate to human support
                  </p>
                  <div className="space-y-2">
                    {config.escalationTriggers.map((trigger, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Input
                          value={trigger}
                          onChange={(e) => {
                            const newTriggers = [...config.escalationTriggers];
                            newTriggers[index] = e.target.value;
                            updateConfig({ escalationTriggers: newTriggers });
                          }}
                          placeholder="e.g., speak to human, manager, complaint"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeArrayItem('escalationTriggers', index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('escalationTriggers', '')}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Escalation Trigger
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>


        </div>
      </div>
    </div>
  );
};
